/**
 * Auth state — два независимых контура (ADMIN_SPEC §0.1).
 *
 *   team*   — команда Skrezo (логин+пароль). Платформенная зона.
 *   client* — клиника / соло (телефон+OTP). Кабинет клиники.
 *
 * Токены/юзеры каждого контура персистятся отдельными ключами (см.
 * `services/utils/auth-storage.ts`) — те же ключи читает `http.ts`,
 * поэтому axios-слой и атомы всегда синхронизированы.
 *
 * `isTeamAuthedAtom` / `isClientAuthedAtom` — derived-гейты для роутов.
 * `logoutAtom` — сбрасывает ОБА контура (жёсткий выход).
 */

import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

import type {ClientUser, TeamUser} from '@services/auth';
import {
  CLIENT_TOKEN_STORAGE_KEY,
  CLIENT_USER_STORAGE_KEY,
  TEAM_TOKEN_STORAGE_KEY,
  TEAM_USER_STORAGE_KEY,
  writeTeamRefresh,
} from '@services/utils';

const persistOpts = {getOnInit: true} as const;

/* ── Команда Skrezo ────────────────────────────────────────────── */
export const teamTokenAtom = atomWithStorage<string | null>(
  TEAM_TOKEN_STORAGE_KEY,
  null,
  undefined,
  persistOpts,
);
export const teamUserAtom = atomWithStorage<TeamUser | null>(
  TEAM_USER_STORAGE_KEY,
  null,
  undefined,
  persistOpts,
);
export const isTeamAuthedAtom = atom((get) => get(teamTokenAtom) !== null);

/* ── Клиент: клиника / соло ────────────────────────────────────── */
export const clientTokenAtom = atomWithStorage<string | null>(
  CLIENT_TOKEN_STORAGE_KEY,
  null,
  undefined,
  persistOpts,
);
export const clientUserAtom = atomWithStorage<ClientUser | null>(
  CLIENT_USER_STORAGE_KEY,
  null,
  undefined,
  persistOpts,
);
export const isClientAuthedAtom = atom((get) => get(clientTokenAtom) !== null);

/* ── Actions ───────────────────────────────────────────────────── */

/** Полный выход из обоих контуров. */
export const logoutAtom = atom(null, (_get, set) => {
  writeTeamRefresh(null);
  set(teamTokenAtom, null);
  set(teamUserAtom, null);
  set(clientTokenAtom, null);
  set(clientUserAtom, null);
});
